import { Pressable } from '../primitives/Pressable.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../primitives/cx.js';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

export type StatPillTone = 'gold' | 'flame' | 'accent';

const toneClass: Record<StatPillTone, string> = {
  gold: 'bg-gold-soft text-gold',
  flame: 'bg-flame-soft text-flame',
  accent: 'bg-accent-soft text-accent',
};

export type StatPillProps = {
  icon: IconName;
  /** Already formatted for the locale — @aj/ui never formats numbers. */
  value: string;
  tone?: StatPillTone;
  /** What tapping it means, for screen readers. */
  label: string;
  onClick?: () => void;
};

/**
 * Power Level and streak, in the top-right corner where a game puts them.
 *
 * A soft tinted ground rather than bare coloured text. On a light theme a warm
 * palette has nowhere to go as small text — every ochre dark enough to clear
 * 4.5:1 on cream is a brown — so the chroma moves to the background, where
 * contrast does not constrain it, and the text stays dark. The tint is faint
 * enough that these still read as numbers rather than badges competing with
 * the habit list underneath.
 *
 * Both stats stay the same size: they are two halves of one identity, and
 * shrinking either one says the wrong thing about which matters.
 */
export function StatPill({ icon, value, tone = 'gold', label, onClick }: StatPillProps) {
  return (
    <Pressable
      aria-label={label}
      disabled={!onClick}
      onClick={onClick}
      className={cx(
        'flex min-h-9 items-center gap-1 rounded-lg px-2',
        'disabled:pointer-events-none disabled:opacity-100',
        toneClass[tone],
      )}
    >
      <Icon name={icon} size={18} />
      <Text variant="heading" numeric className="text-xl font-bold text-current">
        {value}
      </Text>
    </Pressable>
  );
}
